import { Web3Provider } from "@ethersproject/providers";
import { logger } from "../../utils/logger";
import { BigNumber, ethers } from "ethers";
import { useCallback } from "react";
import ERC20ABI from "../../abis/ERC20.json";
import { getRpcUrls } from "../../config/chains";
import { AllCollaterals } from "../../constants/addresses";
import { getPerpetualDexContracts } from "../../constants/perpetual-dex/addresses";
import { ChainIdEnum } from "../../enums/chain";

type PerpetualDexContracts = ReturnType<typeof getPerpetualDexContracts>;

const getReadProviders = (
  provider: Web3Provider | undefined,
  chainId?: ChainIdEnum
): ethers.providers.Provider[] => {
  const rpcProviders = Array.from(new Set(getRpcUrls(chainId).filter(Boolean))).map(
    (rpcUrl) => new ethers.providers.JsonRpcProvider(rpcUrl)
  );

  return provider ? [...rpcProviders, provider] : rpcProviders;
};

const readWithFallback = async <T,>(
  providers: ethers.providers.Provider[],
  read: (provider: ethers.providers.Provider) => Promise<T>
): Promise<T> => {
  let lastError: unknown;

  for (const readProvider of providers) {
    try {
      return await read(readProvider);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("No provider available for unified vault read");
};

export interface UnifiedVaultActionsParams {
  account: string | null | undefined;
  provider: Web3Provider | undefined;
  chainId: ChainIdEnum | undefined;
  contracts: PerpetualDexContracts;
  isMonad: boolean;
  useBalanceManagerDeposit: boolean;
  nativeIsQuote: boolean;
  depositTarget: string | undefined;
  depositContract: ethers.Contract | undefined;
  vaultContract: ethers.Contract | undefined;
  depositTokenContract: ethers.Contract | undefined;
  depositTokenAddress: string | undefined;
  depositTokenIsNative: boolean;
  asset: AllCollaterals;
  decimals: number;
}

/**
 * Compute a gas limit by estimating the call and padding it by 50%, never going
 * below `floor`. The BalanceManager/CLOBEngine deposit and withdraw costs on
 * Monad grew after the BalanceManagerVault afterDeposit lock and the
 * QuantumRiskEngine solvency changes (deposit ~323k, withdraw ~924k), so the old
 * fixed 300k cap now under-funds these calls and they revert out-of-gas.
 * Estimating keeps us correct as costs evolve; the floor is the fallback when an
 * RPC declines to estimate (returns without throwing).
 */
const bufferedGasLimit = async (
  contract: ethers.Contract,
  signature: string,
  args: unknown[],
  floor: number,
  overrides: Record<string, unknown> = {}
): Promise<BigNumber> => {
  const floorBN = BigNumber.from(floor);
  try {
    const est: BigNumber = await contract.estimateGas[signature](
      ...args,
      overrides
    );
    const padded = est.mul(3).div(2); // +50%
    return padded.gt(floorBN) ? padded : floorBN;
  } catch {
    return floorBN;
  }
};

/**
 * Transaction-producing actions for useUnifiedVault (approve, deposit,
 * withdraw, transfer). Extracted verbatim for file-size only.
 */
export const useUnifiedVaultActions = (params: UnifiedVaultActionsParams) => {
  const {
    account,
    provider,
    chainId,
    contracts,
    isMonad,
    useBalanceManagerDeposit,
    nativeIsQuote,
    depositTarget,
    depositContract,
    vaultContract,
    depositTokenContract,
    depositTokenAddress,
    depositTokenIsNative,
    asset,
    decimals,
  } = params;

  /**
   * Approve USDC for the deposit target
   */
  const approveUSDC = useCallback(
    async (amount?: string): Promise<ethers.ContractTransaction | undefined> => {
      if (depositTokenIsNative) {
        return undefined;
      }

      if (!depositTokenContract || !depositTarget || !account) {
        throw new Error("Contract not initialized or wallet not connected");
      }

      const approveAmount = amount || ethers.constants.MaxUint256.toString();

      logger.debug(`[UnifiedVault] Approving ${depositTokenAddress} for ${depositTarget}: ${approveAmount}`);

      const tx = await depositTokenContract.approve(depositTarget, approveAmount);
      await tx.wait(1);

      logger.debug(`[UnifiedVault] Token approved: ${tx.hash}`);
      return tx;
    },
    [
      depositTokenContract,
      depositTarget,
      account,
      depositTokenAddress,
      depositTokenIsNative,
    ]
  );

  /**
   * Get USDC allowance for the deposit target
   */
  const getAllowance = useCallback(
    async (): Promise<string> => {
      if (depositTokenIsNative) {
        return ethers.constants.MaxUint256.toString();
      }

      if (!depositTokenAddress || !depositTarget || !account) {
        return "0";
      }

      try {
        const allowance = await readWithFallback<BigNumber>(
          getReadProviders(provider, chainId),
          (readProvider) =>
            new ethers.Contract(
              depositTokenAddress,
              ERC20ABI,
              readProvider
            ).allowance(account, depositTarget)
        );
        return allowance.toString();
      } catch {
        return "0";
      }
    },
    [
      account,
      chainId,
      depositTarget,
      depositTokenAddress,
      depositTokenIsNative,
      provider,
    ]
  );

  /**
   * Deposit collateral (unified for spot + perp on BalanceManager chains)
   * @param amount - USDC amount in raw decimals (e.g., "10000000000000000000" for 10 USDC on 18-decimal chains)
   */
  const deposit = useCallback(
    async (amount: string): Promise<ethers.ContractTransaction> => {
      if (!depositContract || !account) {
        throw new Error("Contract not initialized or wallet not connected");
      }

      logger.debug(`[Deposit] Depositing: ${amount} (${ethers.utils.formatUnits(amount, decimals)} ${asset}) isMonad=${isMonad} useBalanceManager=${useBalanceManagerDeposit} nativeIsQuote=${nativeIsQuote} nativeDeposit=${depositTokenIsNative}`);

      if (depositTokenIsNative) {
        if (!provider) {
          throw new Error("Provider not initialized");
        }
        const amountBN = BigNumber.from(amount);
        const nativeBalance = await readWithFallback<BigNumber>(
          getReadProviders(provider, chainId),
          (readProvider) => readProvider.getBalance(account)
        );

        if (nativeBalance.lt(amountBN)) {
          throw new Error(`Insufficient native ${asset} balance. Have: ${ethers.utils.formatUnits(nativeBalance, decimals)}, Need: ${ethers.utils.formatUnits(amountBN, decimals)}`);
        }

        const gasLimit = await bufferedGasLimit(
          depositContract,
          "depositNative()",
          [],
          500000,
          { value: amountBN }
        );
        const tx = await depositContract.depositNative({
          value: amountBN,
          gasLimit,
        });
        await tx.wait(1);

        logger.debug(`[Deposit] Native deposit complete: ${tx.hash}`);
        return tx;
      }

      if (!depositTokenContract || !depositTokenAddress) {
        throw new Error("Deposit token not initialized");
      }

      // Pre-flight checks
      const balance = await readWithFallback<BigNumber>(
        getReadProviders(provider, chainId),
        (readProvider) =>
          new ethers.Contract(
            depositTokenAddress,
            ERC20ABI,
            readProvider
          ).balanceOf(account)
      );
      logger.debug(`[Deposit] Token balance: ${balance.toString()} (${ethers.utils.formatUnits(balance, decimals)} ${asset})`);
      if (balance.lt(amount)) {
        throw new Error(`Insufficient ${asset} balance. Have: ${ethers.utils.formatUnits(balance, decimals)}, Need: ${ethers.utils.formatUnits(amount, decimals)}`);
      }

      const allowance = await getAllowance();
      logger.debug(`[Deposit] Current allowance: ${allowance} (${ethers.utils.formatUnits(allowance, decimals)} ${asset})`);
      if (BigNumber.from(allowance).lt(BigNumber.from(amount))) {
        throw new Error(`Insufficient ${asset} allowance. Have: ${ethers.utils.formatUnits(allowance, decimals)}, Need: ${ethers.utils.formatUnits(amount, decimals)}`);
      }

      logger.debug(`[Deposit] All pre-flight checks passed, executing deposit...`);

      let tx: ethers.ContractTransaction;
      if (isMonad || useBalanceManagerDeposit) {
        // CLOBEngine / BalanceManager: deposit(currency, amount, sender, user)
        const gasLimit = await bufferedGasLimit(
          depositContract,
          "deposit(address,uint256,address,address)",
          [depositTokenAddress, amount, account, account],
          600000
        );
        tx = await depositContract.deposit(depositTokenAddress, amount, account, account, { gasLimit });
      } else {
        tx = await depositContract.deposit(amount);
      }
      await tx.wait(1);

      logger.debug(`[Deposit] Deposit complete: ${tx.hash}`);
      return tx;
    },
    [
      depositContract,
      account,
      getAllowance,
      chainId,
      depositTokenContract,
      depositTokenAddress,
      isMonad,
      useBalanceManagerDeposit,
      nativeIsQuote,
      depositTokenIsNative,
      provider,
      decimals,
      asset,
    ]
  );

  /**
   * Withdraw collateral
   * @param amount - USDC amount in raw decimals
   */
  const withdraw = useCallback(
    async (amount: string): Promise<ethers.ContractTransaction> => {
      if (!account) {
        throw new Error("Wallet not connected");
      }

      logger.debug(`[UnifiedVault] Withdrawing: ${amount} (${ethers.utils.formatUnits(amount, decimals)} ${asset})`);

      let tx: ethers.ContractTransaction;
      if (useBalanceManagerDeposit) {
        // BalanceManager user withdraw: withdraw(currency, amount, user) — selector
        // 0x69328dec, defined in BalanceManager.json. (NOTE: 0xc6f1649f is the 4-arg
        // deposit(...), NOT withdraw — calling it here sent a deposit and reverted.)
        // Withdraws the SELECTED collateral from the sender's internal balance to self.
        if (!depositContract) throw new Error("Contract not initialized");
        if (!depositTokenAddress) {
          throw new Error("No token address for the selected withdrawal asset");
        }
        const gasLimit = await bufferedGasLimit(
          depositContract,
          "withdraw(address,uint256,address)",
          [depositTokenAddress, amount, account],
          1500000
        );
        tx = await depositContract["withdraw(address,uint256,address)"](
          depositTokenAddress,
          amount,
          account,
          { gasLimit }
        );
      } else if (isMonad) {
        // CLOBEngine: withdraw(currency, amount, user)
        if (!depositContract) throw new Error("Contract not initialized");
        const gasLimit = await bufferedGasLimit(
          depositContract,
          "withdraw(address,uint256,address)",
          [contracts!.usdc, amount, account],
          1500000
        );
        tx = await depositContract.withdraw(contracts!.usdc, amount, account, {
          gasLimit,
        });
      } else {
        if (!vaultContract) throw new Error("Contract not initialized");
        tx = await vaultContract.withdraw(amount);
      }
      await tx.wait(1);

      logger.debug(`[UnifiedVault] Withdrawal complete: ${tx.hash}`);
      return tx;
    },
    [depositContract, vaultContract, account, isMonad, useBalanceManagerDeposit, contracts, depositTokenAddress, asset, decimals, provider, depositTarget]
  );

  /**
   * Transfer available internal BalanceManager/CLOBEngine balance to another user.
   * @param receiver - Destination account.
   * @param amount - Raw token amount in selected collateral decimals.
   */
  const transferBalance = useCallback(
    async (
      receiver: string,
      amount: string,
      onSubmitted?: (tx: ethers.ContractTransaction) => void
    ): Promise<ethers.ContractTransaction> => {
      if (!account) {
        throw new Error("Wallet not connected");
      }
      if (!isMonad && !useBalanceManagerDeposit) {
        throw new Error("On-chain balance transfer is not supported on this network");
      }
      if (!depositContract || !depositTokenAddress) {
        throw new Error("Contract not initialized");
      }

      logger.debug(
        `[UnifiedVault] Transferring: ${amount} (${ethers.utils.formatUnits(amount, decimals)} ${asset}) to ${receiver}`
      );

      const gasLimit = await bufferedGasLimit(
        depositContract,
        "transfer(address,address,uint256)",
        [depositTokenAddress, receiver, amount],
        600000
      );
      const tx = await depositContract.transfer(
        depositTokenAddress,
        receiver,
        amount,
        { gasLimit }
      );
      onSubmitted?.(tx);
      await tx.wait(1);

      logger.debug(`[UnifiedVault] Transfer complete: ${tx.hash}`);
      return tx;
    },
    [
      account,
      asset,
      decimals,
      depositContract,
      depositTokenAddress,
      isMonad,
      useBalanceManagerDeposit,
    ]
  );

  return {
    approveUSDC,
    getAllowance,
    deposit,
    withdraw,
    transferBalance,
  };
};
